<template>
  <div class="row blog-container">
    <div
      v-if="!isSmallScreen"
      class="col-2 blog-sidebar left-sidebar q-pt-md flex column"
    >
      <div class="logo-wrapper flex justify-center items-center">
        <img
          class="logo cursor-pointer"
          :src="logo"
          :alt="settings.metaFields.metaTitle"
          :width="headerLogoAttributes.width"
          :height="headerLogoAttributes.height"
          @click="router.push({ name: ROUTES.RENDER.BLOG })"
        >
      </div>

      <div class="blog-header-menu-column z-max full-width flex items-end col">
        <RenderHeaderMenu
          class="flex column full-width"
          is-vertical
        />
      </div>
    </div>
    <div class="col">
      <MobileMenuHeader
        v-if="isSmallScreen"
        :logo="logo"
        class="blog-header-mobile"
        :logo-attributes="headerLogoAttributes"
        @click="$refs.mobileMenuRef.toggle()"
      />
      <q-expansion-item
        v-if="isPostPage && isSmallScreen && anchors && anchors.length > 0"
        expand-icon="img:chevron-down.svg"
        class="anchors-area"
        :label="$t('text.tableContent')"
        style="height: 50px; max-width: 100vw;"
      >
        <ul class="anchor-list anchor-list-mobile flex column gap-sm q-px-md q-pb-lg">
          <li
            v-for="anchor in anchors"
            :key="anchor.title"
            class="anchor-item anchor-item-mobile q-pl-md cursor-pointer"
            @click="scrollTo(anchor.element)"
          >
            {{ anchor.title }}
          </li>
        </ul>
      </q-expansion-item>
      <div v-if="!isPostPage && !isSmallScreen && firstPost">
        <a
          class="row bg-card q-mb-md overflow-hidden first-post"
          :href="firstPost.publicUrl"
        >
          <div class="col-7">
            <img
              :src="cover"
              style="width: 100%; height: 100%;"
            >
          </div>
          <div class="col-5 q-pl-lg flex column justify-between">
            <div>
              <div class="fs-34 font-weight-medium q-mb-xs">{{ firstPost.title }}</div>
              <div class="fs-14 opacity-text q-mb-lg flex items-center">
                {{ dayjs.unix(firstPost.createdAt.timestamp).format('DD.MM.YYYY') }}
                &bull;
                {{ dayjs.unix(firstPost.createdAt.timestamp).format('HH:mm') }}
              </div>
            </div>
            <div class="opacity-text">{{ firstPost.metaFields.metaDescription }}</div>
          </div>
        </a>
      </div>
      <div
        :class="[
          'row no-wrap',
          { 'desktop-posts': !isSmallScreen },
          { 'q-mt-lg': isSmallScreen && !anchors?.length },
        ]"
      >
        <div class="q-px-lg full-width">
          <router-view @subscribe="onSubscribe" />
        </div>
        <div
          v-if="!isSmallScreen"
          :class="['col-3 flex column items-center q-px-sm', { 'post-right': isPostPage }]"
        >
          <div :class="['sidebar-content', { 'post-sidebar': isPostPage }]">
            <BlogBanner
              v-if="settings.metaFields.banner"
              :class="['blog-banner', { 'post-banner': isPostPage }]"
              :banner="settings.metaFields.banner"
              column-layout
            />
            <Newsletter
              v-if="isPostPage && settings.isNewsletterEnabled"
              class="newsletter-post"
              :meta-fields="settings.metaFields"
              is-column-layout
              @subscribe="onSubscribe"
            />
          </div>
        </div>
      </div>
      <BlogBanner
        v-if="settings.metaFields.banner && !isPostPage && isSmallScreen"
        class="mobile-banner"
        :banner="settings.metaFields.banner"
        column-layout
      />
      <Newsletter
        v-if="settings.isNewsletterEnabled && !isPostPage"
        :class="{ 'newsletter-form-mobile': $q.screen.width <= 750 }"
        :metaFields="settings.metaFields"
        :is-horizontal="$q.screen.width > 750"
        :is-negative="$q.screen.width > 750"
        is-column-layout
        @subscribe="onSubscribe"
      />
    </div>
    <q-drawer
      v-if="isSmallScreen"
      ref="mobileMenuRef"
      value
      behavior="mobile"
      side="right"
      :width="$q.screen.width"
      class="mobile-menu"
    >
      <MobileMenuHeader
        :logo="logo"
        :logo-attributes="headerLogoAttributes"
        class="mobile-menu-header"
        button-icon="img:x.svg"
        @click="$refs.mobileMenuRef.hide()"
      />
      <RenderHeaderMenu
        class="flex column q-px-lg"
        @click="$refs.mobileMenuRef.hide()"
      />
    </q-drawer>
  </div>
</template>
<script setup>
import dayjs from 'dayjs';
import { computed, ref } from 'vue';
import { ROUTES } from 'src/enums';
import Newsletter from 'components/Newsletter.vue';
import RenderHeaderMenu from 'components/render/RenderHeaderMenu.vue';
import MobileMenuHeader from 'components/MobileMenuHeader.vue';
import BlogBanner from 'components/BlogBanner.vue';
import useRender from 'src/composables/useRender';
import { useRouter } from 'vue-router';
import { useQuasar } from 'quasar';

const $q = useQuasar();
const router = useRouter();

const {
  isSmallScreen,
  logo,
  headerLogoAttributes,
  settings,
  onSubscribe,
  anchors,
  isPostPage,
  firstPost,
} = useRender();

const mobileMenuRef = ref(null);

const cover = computed(() => {
  if (!firstPost.value.coverImageGuid?.url) {
    return '/empty-blog-cover.png';
  }

  return firstPost.value.coverImageGuid?.url;
});
</script>

<style lang="scss" scoped>
.left-sidebar {
  border-right: 1px solid var(--blog-border-color, $grey-4);
  min-height: calc(100vh - 97px);

  .logo-wrapper {
    height: 148px;
    border-bottom: 1px solid var(--blog-border-color, $grey-4);
    position: sticky;
    top: 0;

    .logo {
      max-width: 100px;
      object-fit: contain;
    }
  }
}

.blog-header-menu-column {
  position: sticky;
  top: 185px;
  max-height: calc(100vH - 185px);
}

.post-right {
  padding-top: 64px;
  padding-right: 12px;

  .blog-banner, .newsletter-post {
    width: 100% !important;
  }
}

.blog-banner {
  max-width: 290px;
  margin: 0 auto;
  margin-top: 40px;

  &.post-banner {
    margin-top: 0;
    max-width: unset;
  }
}

.sidebar-content {
  width: 100%;
  position: sticky;
  top: 0;

  &.post-sidebar {
    top: 16px;
  }
}

.first-post {
  padding: 60px 60px 60px 48px;
  color: inherit;
  text-decoration: inherit;
  border-bottom: 1px solid var(--blog-border-color, $grey-4);
}

.fs-34 {
  line-height: 42px;
}

.opacity-text {
  opacity: 0.5;
}

.desktop-posts {
  :deep(.post-card) {
    background: var(--blog-background-color, $grey-1) !important;
  }
}

.newsletter-form-mobile, .mobile-banner {
  max-width: 100% !important;
  margin: 16px 24px !important;
}
</style>
